// Quality Control — local-only data model + the standard Westgard multirule set.
// There is no backend entity for instruments/QC data anywhere in this repo (confirmed by a
// repo-wide search before building this), so everything here is persisted client-side only —
// see useQCData.ts. This file holds the types and the pure statistical rule evaluation.

export interface Instrument {
    id: string;
    name: string;
    manufacturer?: string;
    model?: string;
    department?: string;
    createdAt: string;
}

export interface QCSample {
    id: string;
    instrumentId: string;
    testName: string;
    level: string; // e.g. "Level 1", "Level 2", "Level 3"
    unit?: string;
    targetMean: number;
    targetSD: number;
    createdAt: string;
}

export interface QCRun {
    id: string;
    qcSampleId: string;
    value: number;
    operator: string;
    timestamp: string;
}

export type WestgardRuleId = '1_2s' | '1_3s' | '2_2s' | 'R_4s' | '4_1s' | '10x';

export const WESTGARD_RULE_LABELS: Record<WestgardRuleId, string> = {
    '1_2s': '1-2s (warning)',
    '1_3s': '1-3s (reject)',
    '2_2s': '2-2s (reject)',
    'R_4s': 'R-4s (reject)',
    '4_1s': '4-1s (reject)',
    '10x': '10x (reject)',
};

/** Rules that indicate an out-of-control run requiring rejection, vs. 1-2s which is only a warning trigger */
export const REJECTING_RULES: WestgardRuleId[] = ['1_3s', '2_2s', 'R_4s', '4_1s', '10x'];

export interface QCRunEvaluation {
    run: QCRun;
    zScore: number;
    violations: WestgardRuleId[];
    outOfControl: boolean;
}

export const computeZScore = (value: number, mean: number, sd: number): number =>
    sd === 0 ? 0 : (value - mean) / sd;

/**
 * Evaluate the standard 6-rule Westgard multirule set against a chronologically-ordered
 * (oldest first) list of QC runs for one QC sample/level. Each run is checked against the
 * rules that only require looking backward from that point, matching how QC review works
 * in practice — you evaluate the newest point against its recent history.
 */
export const evaluateWestgardRules = (runs: QCRun[], targetMean: number, targetSD: number): QCRunEvaluation[] => {
    const sorted = [...runs].sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
    const zScores = sorted.map(r => computeZScore(r.value, targetMean, targetSD));

    return sorted.map((run, i) => {
        const z = zScores[i];
        const violations: WestgardRuleId[] = [];

        if (Math.abs(z) > 3) violations.push('1_3s');
        else if (Math.abs(z) > 2) violations.push('1_2s');

        if (i >= 1) {
            const zPrev = zScores[i - 1];
            if ((z > 2 && zPrev > 2) || (z < -2 && zPrev < -2)) violations.push('2_2s');
            if (Math.abs(z - zPrev) > 4 && ((z > 2 && zPrev < -2) || (z < -2 && zPrev > 2))) violations.push('R_4s');
        }

        if (i >= 3) {
            const last4 = zScores.slice(i - 3, i + 1);
            if (last4.every(v => v > 1) || last4.every(v => v < -1)) violations.push('4_1s');
        }

        if (i >= 9) {
            const last10 = zScores.slice(i - 9, i + 1);
            if (last10.every(v => v > 0) || last10.every(v => v < 0)) violations.push('10x');
        }

        const outOfControl = violations.some(v => REJECTING_RULES.includes(v));
        return { run, zScore: z, violations, outOfControl };
    });
};
